import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";

interface RFIData {
  projectId: number | null;
  rfiNumber: string;
  subject: string;
  description: string;
  submittedById: number | null;
  submittedToId: number | null;
  submissionDate: Date | null;
  responseRequiredBy: Date | null;
  priority: string;
  category: string;
  attachments: File[];
  status: string;
  response: string;
  responseDate: Date | null;
  respondedById: number | null;
}

class RFIManagementWizardStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  rfiData: RFIData = {
    projectId: null,
    rfiNumber: "",
    subject: "",
    description: "",
    submittedById: null,
    submittedToId: null,
    submissionDate: new Date(),
    responseRequiredBy: null,
    priority: "Medium",
    category: "General",
    attachments: [],
    status: "Draft",
    response: "",
    responseDate: null,
    respondedById: null,
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.generateRFINumber();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateRFIData = <K extends keyof RFIData>(
    field: K,
    value: RFIData[K]
  ) => {
    this.rfiData[field] = value;
  };

  generateRFINumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    this.rfiData.rfiNumber = `RFI-${timestamp}`;
  };

  selectProject = (projectId: number) => {
    this.rfiData.projectId = projectId;
  };

  addAttachments = (files: FileList) => {
    const newFiles = Array.from(files);
    runInAction(() => {
      this.rfiData.attachments = [...this.rfiData.attachments, ...newFiles];
    });
  };

  removeAttachment = (index: number) => {
    this.rfiData.attachments = this.rfiData.attachments.filter((_, i) => i !== index);
  };

  // Async actions
  submitRFI = async () => {
    this.loading = true;
    this.error = null;

    try {
      const rfisStore = this.rootStore.rfisStore as ServiceStore;

      const rfiData = {
        projectId: this.rfiData.projectId,
        rfiNumber: this.rfiData.rfiNumber,
        subject: this.rfiData.subject,
        description: this.rfiData.description,
        submittedById: this.rfiData.submittedById,
        submittedToId: this.rfiData.submittedToId,
        submissionDate: this.rfiData.submissionDate,
        responseRequiredBy: this.rfiData.responseRequiredBy,
        priority: this.rfiData.priority,
        category: this.rfiData.category,
        attachmentCount: this.rfiData.attachments.length,
        status: "Submitted",
      };

      const newRFI = await rfisStore?.create(rfiData);

      runInAction(() => {
        this.success = "RFI submitted successfully!";
        this.nextStep();
      });

      return newRFI;
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to submit RFI";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.rfiData = {
      projectId: null,
      rfiNumber: "",
      subject: "",
      description: "",
      submittedById: null,
      submittedToId: null,
      submissionDate: new Date(),
      responseRequiredBy: null,
      priority: "Medium",
      category: "General",
      attachments: [],
      status: "Draft",
      response: "",
      responseDate: null,
      respondedById: null,
    };
    this.generateRFINumber();
  };

  // Computed values
  get canProceedFromStep1() {
    return !!this.rfiData.projectId && !!this.rfiData.subject && !!this.rfiData.description;
  }

  get canProceedFromStep2() {
    return !!this.rfiData.submittedToId;
  }

  get canSubmitRFI() {
    return this.canProceedFromStep1 && this.canProceedFromStep2;
  }

  get projects() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return (jobStore?.entities || []).filter((p: any) => p.stage !== "Completed");
  }

  get contacts() {
    const contactsStore = this.rootStore.contactsStore as ServiceStore;
    return contactsStore?.entities || [];
  }

  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get selectedProject() {
    if (!this.rfiData.projectId) return null;
    return this.projects.find((p: any) => p.id === this.rfiData.projectId);
  }

  get priorities() {
    return ["Low", "Medium", "High", "Critical"];
  }

  get categories() {
    return [
      "General",
      "Design Clarification",
      "Material Specification",
      "Construction Method",
      "Schedule",
      "Safety",
      "Code Compliance",
    ];
  }
}

export default RFIManagementWizardStore;